import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import { Root, Label, InputRow, LeadingSlot, Input, TrailingSlot, Affix } from '../FieldRow/FieldRow.styles';

export type AddressFieldProps = {
  label: string;
  /** The local part; the domain is fixed and shown as the trailing affix. */
  value: string;
  onChange: (value: string) => void;
  domain: string;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
};

/**
 * The mailbox address row in the create form's field grammar (see `FieldRow.styles`): a decorative
 * `mail` icon leading, the local-part input, and `@domain` as the trailing affix. No copy button: the
 * value is being typed, not handed over (the password row, prefilled and to be saved, keeps its own).
 * Same row metrics
 * as `PasswordField`, defined once in the shared local styles.
 */
export const AddressField = ({
  label,
  value,
  onChange,
  domain,
  placeholder,
  disabled = false,
  maxLength,
}: AddressFieldProps): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <Root $disabled={disabled}>
      <Label text={label} tag="span" />
      <InputRow>
        <LeadingSlot aria-hidden="true">
          <Icon name="mail" size={16} />
        </LeadingSlot>
        <Input
          type="text"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={value}
          aria-label={t(label)}
          placeholder={placeholder ? t(placeholder) : undefined}
          disabled={disabled}
          maxLength={maxLength}
          onChange={(event) => onChange(event.target.value)}
        />
        <TrailingSlot>
          <Affix title={`@${domain}`}>@{domain}</Affix>
        </TrailingSlot>
      </InputRow>
    </Root>
  );
};

export default AddressField;
